import { LitElement, html, svg, css, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { HistoryEntry, PregamePlayer, PublicCard, PublicState, ReplayFrame } from "./protocol";
import type { BoardView } from "./board/arena-board";
import { entryLines, isPregameDecision, optionValue, PHASE_LABEL, playerName } from "./board/game-text";
import { askToReportIssue } from "./report-issue";
import "./board/arena-board";

const COLORS = ["#d4b0ee", "#edb36b", "#73c8c3", "#e987a0"];
type StepSize = "action" | "phase" | "turn";
type Metric = "life" | "power" | "hand";

export function replayPosition(cards: PublicCard[], frame: ReplayFrame): ReplayFrame {
  const zone = (refs: number[]) => Object.fromEntries(refs.map((i) => [cards[i].object_id, cards[i]]));
  const players = { ...frame.state.players };
  for (const z of frame.zones) {
    const p = players[z.seat];
    if (p) players[z.seat] = { ...p, hand: zone(z.hand), battlefield: zone(z.battlefield), graveyard: zone(z.graveyard),
      exile: zone(z.exile), command_zone: zone(z.command_zone), phased_out: zone(z.phased_out) };
  }
  return { ...frame, state: { ...frame.state, players }, zones: [] };
}

// Recorded decision points only: automatic steps are reflected in the next position.
export function nextReplayIndex(frames: ReplayFrame[], from: number, direction: number, size: StepSize): number {
  const here = frames[from]?.state;
  if (!here) return 0;
  let next = from + direction;
  while (next >= 0 && next < frames.length) {
    const there = frames[next].state;
    if (size === "action" || there.turn !== here.turn || there.active !== here.active ||
      (size === "phase" && there.phase.kind !== here.phase.kind)) return next;
    next += direction;
  }
  return direction < 0 ? 0 : frames.length - 1;
}

export function reviewMetric(frame: ReplayFrame, seat: string, metric: Metric): number {
  const p = frame.state.players[seat];
  if (!p) return 0;
  if (metric === "life") return p.life;
  if (metric === "hand") return p.hand_count;
  return Object.values(p.battlefield).reduce((sum, c) => sum + Math.max(0, optionValue(c.characteristics.power) ?? 0), 0);
}

function scoreOf(frame: ReplayFrame, seat: string): number {
  return frame.scores.find((s) => s.seat === seat)?.points ?? 0;
}

// Bound the display, not the score: the AI's terminal scores would flatten every earlier point.
function advantage(frame: ReplayFrame, seat: string): number {
  if (frame.state.decision.kind === "GameEnded" && optionValue(frame.state.winner) === null) return 0;
  return Math.max(-100, Math.min(100, scoreOf(frame, seat)));
}

@customElement("match-review")
export class MatchReview extends LitElement {
  @property({ attribute: false }) frames: ReplayFrame[] = [];
  @property({ attribute: false }) history: HistoryEntry[] = [];
  @property({ attribute: false }) finalState!: PublicState;
  @property({ attribute: false }) pregame: PregamePlayer[] = [];
  @property({ attribute: false }) reload: () => void = () => {};
  @property() seat = "";
  @property() game = "";
  @property({ type: Number }) startingLife = 20;
  @property({ type: Number }) total = 0;
  @property({ type: Boolean }) loading = false;
  @property() error: string | null = null;
  @property({ type: Number }) received = 0;
  @property({ type: Number }) size = 0;
  @state() private cursor = -1;
  @state() private replaying = false;
  @state() private playing = false;
  @state() private stepSize: StepSize = "action";
  @state() private intervalMs = 800;
  private timer: number | null = null;
  @state() private waitedSeconds = 0;
  private waitTimer: number | null = null;
  private returnFocus: HTMLElement | null = null;

  static styles = css`
    :host { display:block; margin-top:32px; color:var(--text,#ddd); --review-line:#39353d; font-family:var(--font-ui,sans-serif); }
    * { box-sizing:border-box; }
    button, select { font:inherit; color:inherit; }
    button { border:1px solid var(--review-line); background:#29262d; padding:9px 13px; border-radius:6px; cursor: var(--cursor-pointer, pointer); }
    button:hover:not(:disabled) { background:#39313f; border-color:#a083b4; }
    button:disabled { opacity:.35; cursor: var(--cursor-default, default); }
    button:focus-visible, select:focus-visible, input:focus-visible { outline:2px solid #d4b0ee; outline-offset:3px; }
    .primary { background:#d4b0ee; color:#24162e; border-color:#d4b0ee; font-weight:700; }
    button.primary:hover:not(:disabled) { background:#e7ccfa; }
    .eyebrow { font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:#c4a4d6; font-weight:700; }
    h2 { margin:5px 0 8px; font-size:26px; letter-spacing:-.04em; font-weight:600; }
    h3 { margin:0; font-size:13px; font-weight:600; }
    p { color:#aaa2af; font-size:12px; line-height:1.6; margin:0; }
    .heading { display:flex; justify-content:space-between; align-items:center; gap:20px; margin-bottom:22px; }
    .legend { display:flex; gap:16px; flex-wrap:wrap; font-size:11px; color:#c2bac8; }
    .dot { display:inline-block; width:7px; height:7px; border-radius:50%; margin-right:7px; background:var(--seat-color); }
    .analysis { border:1px solid var(--review-line); border-radius:10px; overflow:hidden; background:#211e24; }
    .analysis-top { padding:18px 20px; display:flex; justify-content:space-between; align-items:center; gap:16px; border-bottom:1px solid var(--review-line); }
    .selection { color:#ece4f2; font-size:12px; font-variant-numeric:tabular-nums; }
    .selection small { display:block; color:#a39ba9; margin-top:5px; }
    .charts { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); border-top:1px solid var(--review-line); }
    .chart { min-width:0; padding:18px 18px 10px; }
    .charts .chart + .chart { border-left:1px solid var(--review-line); }
    .chart-head { display:flex; justify-content:space-between; gap:12px; margin-bottom:8px; }
    .chart-head small { font-size:10px; color:#99909f; }
    .chart svg { width:100%; height:130px; display:block; cursor: var(--cursor-default, crosshair); overflow:visible; }
    .advantage-chart svg { height:150px; }
    .plot { position:relative; }
    .y-axis { position:absolute; top:10px; bottom:12px; left:0; display:flex; flex-direction:column; justify-content:space-between; pointer-events:none; font-size:10px; color:#a399aa; }
    .x-axis { display:flex; justify-content:space-between; padding:2px 2% 0 6%; font-size:10px; color:#a399aa; }
    .chart path { fill:none; stroke-width:2; stroke-linejoin:round; vector-effect:non-scaling-stroke; }
    .chart text { fill:#938a9a; font:10px sans-serif; }
    .chart .grid { stroke:#36303c; stroke-width:1; vector-effect:non-scaling-stroke; }
    .chart .cursor { stroke:#e9ddf0; stroke-width:1; stroke-dasharray:3 3; vector-effect:non-scaling-stroke; opacity:.7; }
    .scrub { padding:16px 20px; border-top:1px solid var(--review-line); }
    input[type=range] { width:100%; accent-color:#d4b0ee; cursor: var(--cursor-pointer, pointer); margin:8px 0; }
    .range-labels { display:flex; justify-content:space-between; font-size:10px; color:#aba1b2; }
    .readout { display:flex; gap:18px; flex-wrap:wrap; margin-top:7px; font-size:11px; }
    .readout b { color:var(--seat-color); font-variant-numeric:tabular-nums; }
    .note { padding:13px 20px; border-top:1px solid var(--review-line); font-size:11px; }
    .empty { padding:30px; border:1px solid var(--review-line); border-radius:10px; text-align:center; }
    .empty button { margin-top:14px; }
    .progress { height:3px; margin-top:18px; background:#352b3c; overflow:hidden; }
    .progress span { display:block; height:100%; background:#d4b0ee; transition:width 200ms; }
    .progress.indeterminate span { width:30%; animation:sweep 1.4s ease-in-out infinite; }
    @keyframes sweep { from { transform:translateX(-100%); } to { transform:translateX(340%); } }
    @media (prefers-reduced-motion: reduce) { .progress.indeterminate span { animation:none; width:100%; opacity:.4; } }
    .empty .hint { margin-top:6px; color:#8d8593; }
    .overlay { position:fixed; inset:0; z-index:1000; background:#19171c; display:flex; flex-direction:column; }
    .replay-header { padding:12px 18px; display:flex; align-items:center; justify-content:space-between; gap:16px; border-bottom:1px solid #3b3342; }
    .replay-header h2 { font-size:17px; margin:0; }
    .position { min-width:0; flex:1; }
    .position p { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:75vw; margin-top:4px; }
    .replay-table { flex:1; min-height:0; display:grid; grid-template-columns:58px minmax(0,1fr); overflow:auto; }
    arena-board { display:block; min-height:600px; height:100%; min-width:0; }
    .meter-side { display:flex; flex-direction:column; align-items:center; padding:16px 10px; gap:12px; background:#211c26; min-height:600px; }
    .meter-side small { font-size:9px; color:#a99ab3; text-transform:uppercase; letter-spacing:.1em; }
    .meter-side b { font-size:11px; color:#e8d7f2; font-variant-numeric:tabular-nums; }
    .meter { width:16px; flex:1; min-height:240px; position:relative; border:1px solid #746579; border-radius:3px; overflow:hidden; display:flex; flex-direction:column; }
    .meter > span { display:block; transition:height 200ms; }
    .meter::after { content:''; position:absolute; top:50%; left:0; right:0; border-top:1px solid #19141d; }
    .controls { border-top:1px solid #3b3342; padding:10px 18px; background:#211c26; }
    .transport { display:flex; gap:8px; align-items:center; justify-content:center; flex-wrap:wrap; }
    select { border:1px solid #504358; border-radius:5px; background:#211c26; padding:8px; font-size:12px; }
    .counter { color:#b7a8c2; font-size:11px; font-variant-numeric:tabular-nums; min-width:100px; text-align:center; }
    .replay-scrub { display:flex; align-items:center; gap:14px; }
    .replay-scrub input { flex:1; min-width:50px; }
    .replay-scrub small { font-size:10px; color:#a89caf; white-space:nowrap; }
    .keys { color:#9988a5; font-size:10px; }
    @media(max-width:720px) {
      .charts { grid-template-columns:1fr; }
      .charts .chart + .chart { border-left:0; border-top:1px solid var(--review-line); }
      .heading { align-items:flex-start; }
      .analysis-top { align-items:flex-start; flex-direction:column; }
      .heading .primary { white-space:nowrap; }
      h2 { font-size:22px; }
      .replay-table { grid-template-columns:40px minmax(0,1fr); }
      .meter-side { padding:10px 5px; }
      .keys { display:none; }
    }
    @media(prefers-reduced-motion:reduce) { * { transition:none !important; } }
  `;

  private get index(): number { return Math.max(0, Math.min(this.cursor < 0 ? this.frames.length - 1 : this.cursor, this.frames.length - 1)); }
  private get frame(): ReplayFrame | undefined { return this.frames[this.index]; }
  private get seats(): string[] { return this.finalState.turn_order; }
  private get perspective(): string { return this.seats.includes(this.seat) ? this.seat : this.seats[0]; }
  private get ready(): boolean { return !this.loading && !this.error && this.frames.length > 0 && this.frames.length === this.total; }
  private name(seat: string): string { return playerName(this.finalState, seat); }
  private positionLabel(frame: ReplayFrame): string {
    return isPregameDecision(frame.state.decision) ? "Opening hands" : `Turn ${frame.state.round} · ${PHASE_LABEL[frame.state.phase.kind]}`;
  }

  private description(index: number): string {
    const entry = this.history[this.frames[index]?.index - 1];
    if (!entry) return "Opening position · choose who plays first";
    const words = entryLines(this.finalState, entry).map((line) => `${line.actor} ${line.parts.map((p) => typeof p === "string" ? p : p.characteristics.name).join("")}`).join(" · ");
    return words || `${this.name(entry.seat)} passed priority`;
  }

  private stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.playing = false;
  }
  private seek(index: number): void {
    this.stop();
    this.cursor = Math.max(0, Math.min(index, this.frames.length - 1));
  }
  private step(direction: number): void { this.seek(nextReplayIndex(this.frames, this.index, direction, this.stepSize)); }
  private togglePlay(): void {
    if (this.playing) { this.stop(); return; }
    if (this.index === this.frames.length - 1) this.cursor = 0;
    this.playing = true;
    this.timer = window.setInterval(() => {
      this.cursor = nextReplayIndex(this.frames, this.index, 1, this.stepSize);
      if (this.index === this.frames.length - 1) this.stop();
    }, this.intervalMs);
  }
  private openReplay(fromStart = false): void {
    if (!this.ready) return;
    this.returnFocus = this.shadowRoot?.activeElement as HTMLElement | null;
    if (fromStart) this.seek(0);
    this.replaying = true;
    void this.updateComplete.then(() => this.shadowRoot?.querySelector<HTMLElement>(".overlay")?.focus());
  }
  private reportIssue(e: Event): void {
    this.stop();
    const frame = this.frame;
    askToReportIssue(e.currentTarget!, [["Replay position", `${this.index} / ${this.frames.length - 1}${frame ? ` · ${this.positionLabel(frame)}` : ""}`]]);
  }
  private closeReplay(): void { this.stop(); this.replaying = false; this.returnFocus?.focus(); }
  disconnectedCallback(): void { this.stop(); this.stopWaitClock(); super.disconnectedCallback(); }

  protected updated(): void {
    if (this.loading && !this.ready && this.waitTimer === null) {
      this.waitedSeconds = 0;
      this.waitTimer = window.setInterval(() => { this.waitedSeconds += 1; }, 1000);
    } else if (!this.loading && this.waitTimer !== null) this.stopWaitClock();
  }

  private stopWaitClock(): void {
    if (this.waitTimer !== null) window.clearInterval(this.waitTimer);
    this.waitTimer = null;
  }

  private loadingProgress(): { detail: string; fraction: number | null } {
    const mb = (bytes: number) => (bytes / 1_000_000).toFixed(1);
    if (this.total) return { detail: `${this.frames.length.toLocaleString()} of ${this.total.toLocaleString()} positions loaded`, fraction: this.frames.length / this.total };
    if (this.received) return { detail: `Downloading the review · ${mb(this.received)}${this.size ? ` of ${mb(this.size)}` : ""} MB`, fraction: this.size ? Math.min(1, this.received / this.size) : null };
    const actions = this.history.length ? `Replaying ${this.history.length.toLocaleString()} actions` : "Restoring the board from the saved game log";
    return { detail: `${actions} · ${this.waitedSeconds}s`, fraction: null };
  }

  private renderLoading() {
    if (this.error) return html`<div class="empty" role="status"><h3>Replay unavailable</h3><p>${this.error}</p><button @click=${this.reload}>Try again</button></div>`;
    const { detail, fraction } = this.loadingProgress();
    return html`<div class="empty" role="status"><h3>Reconstructing your match…</h3><p>${detail}</p>
      ${this.waitedSeconds >= 5 && fraction === null ? html`<p class="hint">A long game's first review takes a while to build. It's saved after that, so it opens quickly next time.</p>` : nothing}
      <div class=${fraction === null ? "progress indeterminate" : "progress"} role="progressbar" aria-label="Review loading" aria-valuemin="0" aria-valuemax="100" aria-valuenow=${fraction === null ? nothing : Math.round(fraction * 100)}><span style=${fraction === null ? "" : `width:${fraction * 100}%`}></span></div></div>`;
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") { e.preventDefault(); this.closeReplay(); return; }
    const target = e.composedPath()[0];
    if (e.key === "Tab") {
      const buttons = [...this.shadowRoot!.querySelectorAll<HTMLElement>(".overlay button:not(:disabled), .overlay select, .overlay input")];
      const at = buttons.indexOf(target as HTMLElement);
      if (e.shiftKey && at <= 0) { e.preventDefault(); buttons.at(-1)?.focus(); }
      else if (!e.shiftKey && (at === buttons.length - 1 || at === -1)) { e.preventDefault(); buttons[0]?.focus(); }
      return;
    }
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLButtonElement) return;
    if (["ArrowRight", "ArrowLeft", " ", "Home", "End"].includes(e.key)) e.preventDefault();
    if (e.key === "ArrowRight") this.step(1);
    if (e.key === "ArrowLeft") this.step(-1);
    if (e.key === " ") this.togglePlay();
    if (e.key === "Home") this.seek(0);
    if (e.key === "End") this.seek(this.frames.length - 1);
  };

  private chartClick(e: MouseEvent): void {
    const bounds = (e.currentTarget as SVGElement).getBoundingClientRect();
    const fraction = ((e.clientX - bounds.left) / bounds.width * 500 - 30) / 460;
    this.seek(Math.round(fraction * (this.frames.length - 1)));
  }
  private plot(label: string, metric: Metric | "advantage"): TemplateResult {
    const series = metric === "advantage" ? [this.perspective] : this.seats;
    const values = (seat: string) => this.frames.map((f) => metric === "advantage" ? advantage(f, seat) : reviewMetric(f, seat, metric));
    const all = series.flatMap(values);
    const low = metric === "advantage" ? -100 : all.reduce((min, n) => Math.min(min, n), 0);
    const high = metric === "advantage" ? 100 : all.reduce((max, n) => Math.max(max, n), 1);
    const x = (i: number) => 30 + i / Math.max(1, this.frames.length - 1) * 460;
    const y = (v: number) => 100 - (v - low) / (high - low) * 90;
    const color = (seat: string) => COLORS[this.seats.indexOf(seat) % COLORS.length];
    return html`<section class="chart ${metric === "advantage" ? "advantage-chart" : ""}">
      <div class="chart-head"><h3>${label}</h3><small>${metric === "advantage" ? "AI estimate · points" : metric === "power" ? "Total creature power" : ""}</small></div>
      <div class="plot"><div class="y-axis" aria-hidden="true"><span>${high}</span><span>${Math.round((low + high) / 2)}</span><span>${low}</span></div>
      <svg viewBox="0 0 500 110" preserveAspectRatio="none" role="img" aria-label=${`${label} across the match. Use the position slider to inspect values.`} @click=${this.chartClick}>
        ${[low, (low + high) / 2, high].map((v) => svg`<line class="grid" x1="30" x2="490" y1=${y(v)} y2=${y(v)}></line>`)}
        ${series.map((seat) => svg`<path stroke=${color(seat)} d=${values(seat).map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(" ")}></path>`)}
        <line class="cursor" x1=${x(this.index)} x2=${x(this.index)} y1="8" y2="102"></line>
        ${series.map((seat) => svg`<circle cx=${x(this.index)} cy=${y(values(seat)[this.index])} r="3" fill=${color(seat)}></circle>`)}
      </svg></div><div class="x-axis"><span>Opening</span><span>Turn ${this.finalState.round}</span></div>
      <div class="readout">${series.map((seat) => html`<span style=${`--seat-color: ${color(seat)}`}><b>${metric === "advantage" ? this.scoreLabel(this.frame!, seat) : reviewMetric(this.frame!, seat, metric)}</b> ${this.name(seat)}</span>`)}</div>
    </section>`;
  }
  private scoreLabel(frame: ReplayFrame, seat: string): string {
    const score = scoreOf(frame, seat);
    if (score >= 1000000) return "Won";
    if (score <= -1000000) return "Lost";
    if (frame.state.decision.kind === "GameEnded" && optionValue(frame.state.winner) === null) return "Draw";
    return `${score > 0 ? "+" : ""}${score}`;
  }
  private boardView(frame: ReplayFrame): BoardView {
    const state = frame.state;
    const names = Object.fromEntries(this.seats.map((seat) => [seat, this.finalState.players[seat]?.name ?? seat]));
    return {
      game: this.game, seat: this.seat, state: { ...state, players: Object.fromEntries(Object.entries(state.players).map(([seat, p]) => [seat, { ...p, name: names[seat] ?? p.name }])) },
      pacing: null, history: this.history.slice(0, frame.index), moves: [], error: null, autoPay: false, autoPaying: false,
      aiLevels: {}, catchingUp: false, walking: null, turnBannerMs: 0, startingLife: this.startingLife,
      undoable: null, clocks: null, pregame: this.pregame, previewAt: "auto", looks: {}, seatLooks: {}, battlefield: null,
    };
  }
  private renderReplay(): TemplateResult | typeof nothing {
    const frame = this.frame;
    if (!this.replaying || !frame) return nothing;
    const points = advantage(frame, this.perspective);
    const share = frame.state.decision.kind === "GameEnded" && optionValue(frame.state.winner) === null ? 50 : 50 + points * .45;
    const mineColor = COLORS[this.seats.indexOf(this.perspective) % COLORS.length];
    const opponent = this.seats.find((s) => s !== this.perspective) ?? this.perspective;
    const theirColor = COLORS[this.seats.indexOf(opponent) % COLORS.length];
    return html`<div class="overlay" role="dialog" aria-modal="true" aria-label="Match replay" tabindex="0" @keydown=${this.onKey}>
      <header class="replay-header">
        <div class="position"><h2><span class="eyebrow">Replay</span> &nbsp; ${this.positionLabel(frame)}</h2><p aria-live="polite">${this.description(this.index)}</p></div>
        <span class="keys">← → step · Space play · Esc close</span><button @click=${this.reportIssue}>Report issue</button><button @click=${this.closeReplay}>Close replay</button>
      </header>
      <div class="replay-table">
        <aside class="meter-side" title=${`AI heuristic for ${this.name(this.perspective)} versus ${this.seats.length > 2 ? "the average opponent" : this.name(opponent)}. Positive points favor ${this.name(this.perspective)}. Not a win probability.`}>
          <small>Edge</small><b>${this.scoreLabel(frame, this.perspective)}</b>
          <div class="meter" role="img" aria-label=${`Estimated advantage for ${this.name(this.perspective)}: ${this.scoreLabel(frame, this.perspective)} points`}>
            <span style=${`height: ${100 - share}%; background: ${theirColor}`}></span><span style=${`height: ${share}%; background: ${mineColor}`}></span>
          </div><small>AI est.</small><b style=${`color:${mineColor}`} title=${this.name(this.perspective)}>${this.name(this.perspective).slice(0, 2).toUpperCase()}</b>
        </aside><arena-board .view=${this.boardView(frame)} .intents=${null}></arena-board>
      </div>
      <footer class="controls">
        <div class="transport">
          <button title="Opening position" aria-label="Jump to opening position" ?disabled=${this.index === 0} @click=${() => this.seek(0)}>⏮</button>
          <button aria-label=${`Previous ${this.stepSize}`} ?disabled=${this.index === 0} @click=${() => this.step(-1)}>← Previous</button>
          <button class="primary" @click=${this.togglePlay}>${this.playing ? "Ⅱ Pause" : "▶ Play"}</button>
          <button aria-label=${`Next ${this.stepSize}`} ?disabled=${this.index === this.frames.length - 1} @click=${() => this.step(1)}>Next →</button>
          <button title="Final position" aria-label="Jump to final position" ?disabled=${this.index === this.frames.length - 1} @click=${() => this.seek(this.frames.length - 1)}>⏭</button>
          <select aria-label="Step through replay by" .value=${this.stepSize} @change=${(e: Event) => { this.stop(); this.stepSize = (e.target as HTMLSelectElement).value as StepSize; }}><option value="action">Every action</option><option value="phase">Every phase</option><option value="turn">Every turn</option></select>
          <select aria-label="Replay playback speed" .value=${String(this.intervalMs)} @change=${(e: Event) => { this.stop(); this.intervalMs = Number((e.target as HTMLSelectElement).value); }}><option value="1600">0.5×</option><option value="800">1×</option><option value="400">2×</option><option value="200">4×</option></select>
        </div>
        <div class="replay-scrub"><small>Opening</small><input type="range" aria-label="Replay position" aria-valuetext=${this.description(this.index)} min="0" max=${this.frames.length - 1} .value=${String(this.index)} @input=${(e: Event) => this.seek(Number((e.target as HTMLInputElement).value))}><small class="counter">${this.index} / ${this.frames.length - 1} actions</small></div>
      </footer>
    </div>`;
  }
  render(): TemplateResult {
    return html`
      <header class="heading"><div><span class="eyebrow">Match review</span><h2>See where the game turned.</h2><p>Follow the resources. Revisit the decisions.</p></div><button class="primary" ?disabled=${!this.ready} @click=${() => this.openReplay(true)}>▶ Replay match</button></header>
      ${!this.ready ? this.renderLoading() : html`
        <div class="analysis">
          <div class="analysis-top"><div class="selection">${this.positionLabel(this.frame!)}<small>${this.description(this.index)}</small></div><div class="legend">${this.seats.map((seat, i) => html`<span style=${`--seat-color: ${COLORS[i % COLORS.length]}`}><i class="dot"></i>${this.name(seat)}${seat === this.seat ? " (you)" : ""}</span>`)}</div></div>
          ${this.plot(`Advantage · ${this.name(this.perspective)}`, "advantage")}
          <div class="charts">${this.plot("Life totals", "life")}${this.plot("Battlefield strength", "power")}${this.plot("Cards in hand", "hand")}</div>
          <div class="scrub"><div class="range-labels"><span>Inspect any point in the match</span><span>${this.index} / ${this.frames.length - 1} actions</span></div><input type="range" aria-label="Analysis position" min="0" max=${this.frames.length - 1} .value=${String(this.index)} @input=${(e: Event) => this.seek(Number((e.target as HTMLInputElement).value))}><button @click=${() => this.openReplay()}>View this position →</button></div>
          <p class="note">Advantage uses the AI’s heuristic for life, cards, permanents and combat exposure${this.seats.length > 2 ? " versus the average opponent" : ""}. It is an estimate, not a win probability. Graphs cap it at ±100 points. Replay steps follow recorded decisions; automatic phases appear in the next position.</p>
        </div>`}
      ${this.renderReplay()}`;
  }
}
